/**
 * 司机端 — 任务详情（司机只可查看本人运输单 / 装车清点确认 / 查看轨迹）
 * N6(2026-09-04)：列表/详情/确认均走司机身份接口 /transit/driver/orders[/:id][/confirm]。
 * 说明：起运/到达/签收属企业调度端操作（/transit/orders/:id/depart|arrive|sign，
 * 需 transit:write 权限），司机端不越权展示该等按钮。
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { confirmDriverOrder, fetchDriverOrder, fetchDriverTrack } from '@/api/transit';
import TrackMap from '@/components/common/TrackMap';
import { Empty, Loading, OrderStatusTag } from '@/components/common';
import type { GPSPoint, TransitOrder } from '@/types';
import { fmtTime } from '@/utils/format';

export default function DriverOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const transitId = Number(id ?? 0);

  const [order, setOrder] = useState<TransitOrder | null>(null);
  const [points, setPoints] = useState<GPSPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    setLoading(true);
    fetchDriverOrder(transitId)
      .then((o) => {
        setOrder(o);
        setPoints([]);
        setLoading(false);
        // 轨迹尽力而为：轨迹读取失败不阻断详情展示
        fetchDriverTrack(transitId)
          .then((t) => setPoints(t))
          .catch(() => setPoints([]));
      })
      .catch((e) => {
        // 非本单司机 / 权限不足：明确报错，不静默降级
        setLoading(false);
        setOrder(null);
        alert(e instanceof Error ? e.message : '加载任务失败');
      });
  };

  useEffect(() => {
    if (!transitId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitId]);

  if (loading) return <Loading />;
  if (!order) return <Empty text="任务不存在或无权查看" />;

  const act = async (fn: () => Promise<void>, msg: string) => {
    setBusy(true);
    try {
      await fn();
      alert(msg);
    } catch (e) {
      alert(e instanceof Error ? e.message : '操作失败');
    } finally {
      setBusy(false);
      reload();
    }
  };

  return (
    <div className="driver-page">
      <Link to="/driver/orders" style={{ fontSize: 13 }}>
        ← 返回任务列表
      </Link>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="row-between">
          <div>
            <div className="page-title">
              {order.origin_city} → {order.destination_city}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-ink-secondary)' }}>{order.transit_no}</div>
          </div>
          <OrderStatusTag status={order.status} />
        </div>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-title">行程信息</div>
        <div style={{ fontSize: 13, lineHeight: 2 }}>
          <div>牛只数量：{order.cattle_count} 头</div>
          <div>车辆：{order.vehicle?.plate_no ?? `#${order.vehicle_id}`}</div>
          <div>起运时间：{fmtTime(order.actual_depart_time)}</div>
          <div>到达时间：{fmtTime(order.actual_arrive_time)}</div>
          <div>最近位置：{order.last_lng.toFixed(4)}, {order.last_lat.toFixed(4)}</div>
        </div>
      </div>

      {/* 司机操作（仅装车清点确认属司机职责） */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-title">任务操作</div>
        {order.status === 0 && (
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={busy}
            onClick={() => act(() => confirmDriverOrder(transitId), '装车清点确认成功')}
          >
            装车清点确认
          </button>
        )}
        {order.status === 1 && (
          <div style={{ textAlign: 'center', color: 'var(--color-amber-dark, #b45309)', fontWeight: 700 }}>🚚 运输中，请保持轨迹上报</div>
        )}
        {order.status === 2 && (
          <div style={{ textAlign: 'center', color: 'var(--color-ink-secondary)', fontWeight: 700 }}>📍 已到达，等待调度签收确认</div>
        )}
        {order.status >= 3 && (
          <div style={{ textAlign: 'center', color: 'var(--color-green-dark)', fontWeight: 700 }}>✅ 本单已完成</div>
        )}
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-title">轨迹（{points.length} 点）</div>
        <TrackMap points={points} height={260} />
      </div>
    </div>
  );
}
